import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingController } from '@ionic/angular';
import { capitalizeFirstLetter, Invoker, ViewStatus } from '@ubie/core';
import { shareReplay } from 'rxjs';
import { BulkActionSelectorButton } from '../../interfaces/bulk-action-selector-button.interface';
import { BulkActionSelectorMessages } from '../../interfaces/bulk-action-selector-messages.interface';

@Component({
  selector: 'shared-bulk-action-selector',
  standalone: false,
  templateUrl: './bulk-action-selector.component.html',
  styleUrl: './bulk-action-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkActionSelectorComponent implements OnDestroy {
  private readonly invoker = new Invoker();

  private readonly loadingCtrl = inject(LoadingController);

  readonly buttons = input.required<BulkActionSelectorButton[]>();
  readonly page = input.required<string>();
  readonly amountSelected = input<number>(0);

  @Output() readonly bulkActionSelected = new EventEmitter<string | null>();
  @Output() readonly bulkActionSuccess = new EventEmitter<string>();
  @Output() readonly bulkActionFail = new EventEmitter<string>();

  readonly bulkActionStatus$ = this.invoker.status$.pipe(shareReplay());
  readonly ViewStatus = ViewStatus;
  readonly title = computed(() => `${this.page()}_bulkTitle`);
  private readonly currentStatus = signal(ViewStatus.Init);

  readonly selectedBulkAction = signal<string | null>(null);
  readonly isOpen = computed(() => this.currentStatus() === ViewStatus.Loading);

  readonly isOkButtonDisabled = computed(() => this.amountSelected() === 0);

  readonly bulkActionMessages = computed(() =>
    this.buttons().map((button) => {
      const titleCaseActionName = capitalizeFirstLetter(
        button.bulkAction.toString(),
      );
      return {
        bulkAction: button.bulkAction,
        executing: `${this.page()}_bulk${titleCaseActionName}Executing`,
        singleSelected: `${this.page()}_bulk${titleCaseActionName}OneSelected`,
        multipleSelected: `${this.page()}_bulk${titleCaseActionName}MultipleSelected`,
      } as BulkActionSelectorMessages;
    }),
  );

  readonly currentBulkActionMessage = computed(() => {
    return this.bulkActionMessages().find(
      (messages) => messages.bulkAction === this.selectedBulkAction(),
    );
  });

  constructor() {
    this.bulkActionStatus$.pipe(takeUntilDestroyed()).subscribe((status) => {
      const bulkAction = this.selectedBulkAction();
      if (bulkAction === null) return;
      this.currentStatus.set(status);

      switch (status) {
        case ViewStatus.Resolved:
          this.setCurrentAction(null);
          this.bulkActionSuccess.emit(bulkAction);
          break;
        case ViewStatus.Rejected:
          this.bulkActionFail.emit(bulkAction);
          break;
      }
    });
  }
  async ngOnDestroy(): Promise<void> {
    const top = await this.loadingCtrl.getTop();
    if (top?.id === 'bulk-action-selector-loader') {
      this.loadingCtrl.dismiss('bulk-action-selector-loader');
    }
  }

  setCurrentAction(action: string | null): void {
    this.selectedBulkAction.set(action);

    this.bulkActionSelected.emit(action);
  }

  cancelBulkAction(): void {
    this.setCurrentAction(null);
  }

  invoke(): void {
    const invokedButton = this.buttons().find(
      (button) => button.bulkAction === this.selectedBulkAction(),
    );

    if (invokedButton) {
      this.invoker.setActual(invokedButton.command);
      this.invoker.execute();
    }
  }
}
